import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { api, Capabilities, formatDate, ServiceDetail, TransferInfo } from '../../api';
import Footer from '../../Components/Footer';
import { Alert, Loading, Spinner, StatusTag, useAction, useLoad } from '../../Components/ui';
import BackupsTab from './BackupsTab';
import DatabaseTab from './DatabaseTab';
import DnsTab from './DnsTab';
import EmailTab from './EmailTab';
import FilesTab from './FilesTab';
import OverviewTab from './OverviewTab';
import SslTab from './SslTab';
import WordPressTab from './WordPressTab';

export interface TabProps {
    base: string; // API path of the service, e.g. /services/12
    detail: ServiceDetail;
    reload: () => Promise<void>;
}

type TabId = 'overview' | 'wordpress' | 'dns' | 'email' | 'database' | 'files' | 'ssl' | 'backups';

// `feature` tabs only show when the server enables them; the CarbonEdge B2B API does not offer them yet.
const TABS: Record<TabId, { label: string; component: (props: TabProps) => JSX.Element; feature?: keyof Capabilities }> = {
    overview: { label: 'Overview', component: OverviewTab },
    wordpress: { label: 'WordPress', component: WordPressTab },
    dns: { label: 'DNS', component: DnsTab },
    email: { label: 'Email', component: EmailTab },
    database: { label: 'Database', component: DatabaseTab },
    files: { label: 'Files', component: FilesTab, feature: 'files' },
    ssl: { label: 'SSL', component: SslTab, feature: 'ssl' },
    backups: { label: 'Backups', component: BackupsTab },
};

const TABS_BY_TYPE: Record<ServiceDetail['service']['type'], TabId[]> = {
    // Web packages are finished hosting; DNS is managed in the domain or DNS package.
    wordpress: ['overview', 'wordpress', 'email', 'database', 'files', 'ssl', 'backups'],
    static: ['overview', 'email', 'database', 'files', 'ssl', 'backups'],
    // Mail is part of the web packages only: a domain or DNS zone may point its MX elsewhere.
    domain: ['overview', 'dns'],
    dns: ['overview', 'dns'],
    // Extra databases are managed in their web package's Database tab; their own page shows status/retry.
    database: ['overview'],
};

const PROVISIONING_POLL_MS = 3000;
// Transfers move in hours or days; the server checks CarbonEdge about once a minute.
const TRANSFER_POLL_MS = 30000;

const TRANSFER_STEPS: Array<{ status: TransferInfo['status']; label: string; text: string }> = [
    { status: 'pending', label: 'Submitted', text: 'The transfer request has been sent to the registry.' },
    { status: 'awaiting_approval', label: 'Awaiting approval', text: 'Your current registrar may email the domain owner to approve the transfer. Approving it speeds things up; otherwise it completes automatically within about 5 days.' },
    { status: 'completed', label: 'Completed', text: 'The domain is registered with Forget-IT.' },
];

function TransferProgress({ domain, transfer }: { domain: string | null; transfer: TransferInfo }) {
    const current = TRANSFER_STEPS.findIndex(step => step.status === transfer.status);
    return (
        <div className="panel-card">
            <div className="panel-card-header">
                <h3>Transferring {domain} to Forget-IT</h3>
                <Spinner small />
            </div>
            <p className="card-text" style={{ marginBottom: '1rem' }}>Started {formatDate(transfer.startedAt)}. We email you when the transfer is complete.</p>
            <ol className="transfer-steps">
                {TRANSFER_STEPS.map((step, i) => (
                    <li key={step.status} className={i < current ? 'done' : i === current ? 'current' : ''}>
                        <strong>{step.label}</strong>
                        {i === current && <span>{step.text}</span>}
                    </li>
                ))}
            </ol>
        </div>
    );
}

function RetryTransfer({ base, onDone }: { base: string; onDone: () => Promise<void> }) {
    const [authCode, setAuthCode] = useState('');
    const { busy, error, run } = useAction();
    const submit = (e: FormEvent) => {
        e.preventDefault();
        void run(async () => { await api.post(`${base}/retry`, { authCode }); await onDone(); });
    };
    return (
        <form className="fi-form" onSubmit={submit}>
            {error && <Alert type="error">{error}</Alert>}
            <label>
                New auth code
                <input type="password" className="mono" value={authCode} onChange={e => setAuthCode(e.target.value.trim())} required minLength={6} autoComplete="off" />
                <span className="hint">Get a fresh auth code from your current registrar and make sure the domain is unlocked there.</span>
            </label>
            <button className="btn-submit inline" disabled={busy}>{busy ? 'Retrying…' : 'Retry transfer'}</button>
        </form>
    );
}

/** Remounts the panel per service, so tab state and data never carry over (e.g. a database redirecting to its site). */
export default function ControlPanel() {
    const { serviceId } = useParams();
    return <ServicePanel key={serviceId} />;
}

function ServicePanel() {
    const { serviceId } = useParams();
    const base = `/services/${serviceId}`;
    const { data: detail, error, loading, reload } = useLoad(
        () => api.get<ServiceDetail>(base).then(r => r.data), [base]);
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState<TabId>((searchParams.get('tab') as TabId | null) ?? 'overview');
    const retry = useAction();

    // CarbonEdge provisions in the background; refresh until the service leaves "provisioning".
    const provisioning = detail?.service.status === 'provisioning';
    const transferring = provisioning && !!detail?.service.transfer;
    useEffect(() => {
        if (!provisioning) return;
        const timer = setInterval(() => void reload(), transferring ? TRANSFER_POLL_MS : PROVISIONING_POLL_MS);
        return () => clearInterval(timer);
    }, [provisioning, transferring, reload]);

    if (loading && !detail) return <section className="section page-section"><Loading /></section>;
    if (error || !detail) {
        return (
            <section className="section page-section">
                <Alert type="error">{error ?? 'Service not found'}</Alert>
                <Link className="btn-outline" to="/account">Back to my account</Link>
            </section>
        );
    }

    const { service, capabilities } = detail;
    // A live extra database opens its web package's Database tab.
    if (service.type === 'database' && detail.parentService && ['active', 'suspended'].includes(service.status)) {
        return <Navigate to={`/panel/${detail.parentService.id}?tab=database`} replace />;
    }
    const tabs = TABS_BY_TYPE[service.type].filter(id => !TABS[id].feature || capabilities[TABS[id].feature!]);
    const Active = TABS[tabs.includes(tab) ? tab : 'overview'].component;

    return (
        <>
            <section className="section page-section">
                <div className="panel-header">
                    <div>
                        <span className="section-label"><Link className="text-link" to="/account">My account</Link> / Control panel</span>
                        <h1 className="page-title">{service.domain}</h1>
                        <div className="btn-row">
                            <span className="tag tag-category">{service.productName ?? service.type}</span>
                            <StatusTag status={service.status} />
                        </div>
                    </div>
                </div>

                {detail.owner && (
                    <div className="admin-banner">
                        <span>
                            <strong>Admin view</strong> — this service belongs to {detail.owner.name} ({detail.owner.email}).
                            Changes here affect the customer's live service.
                        </span>
                        <Link className="btn-outline small" to={`/admin/customers/${detail.owner.id}`}>Back to customer</Link>
                    </div>
                )}

                {service.status === 'provisioning' && service.transfer && (
                    <TransferProgress domain={service.domain} transfer={service.transfer} />
                )}

                {service.status === 'provisioning' && !service.transfer && (
                    <div className="panel-card payment-status">
                        <Spinner />
                        <h3>Setting up your service…</h3>
                        <p>CarbonEdge is provisioning your service. This usually takes less than a minute — this page updates by itself.</p>
                    </div>
                )}

                {service.status === 'failed' && service.transfer && (
                    <div className="panel-card">
                        <h3>Transfer failed</h3>
                        <Alert type="error">{service.lastError ?? 'The transfer did not complete'}</Alert>
                        <RetryTransfer base={base} onDone={reload} />
                    </div>
                )}

                {service.status === 'failed' && !service.transfer && (
                    <div className="panel-card">
                        <h3>Setup failed</h3>
                        <Alert type="error">{service.lastError ?? 'Unknown error'}</Alert>
                        {retry.error && <Alert type="error">{retry.error}</Alert>}
                        <button className="btn-submit inline" disabled={retry.busy}
                            onClick={() => retry.run(async () => { await api.post(`${base}/retry`); await reload(); })}>
                            {retry.busy ? 'Retrying…' : 'Retry setup'}
                        </button>
                    </div>
                )}

                {service.status === 'suspended' && (
                    <Alert type="error">This service is suspended. Contact us if you think this is a mistake.</Alert>
                )}

                {service.status === 'cancelled' && (
                    <Alert type="info">This service has been terminated.</Alert>
                )}

                {service.status === 'active' && (
                    <div className="panel-layout">
                        <nav className="panel-nav">
                            {tabs.map(id => (
                                <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>{TABS[id].label}</button>
                            ))}
                        </nav>
                        <div className="panel-content">
                            <Active base={base} detail={detail} reload={reload} />
                        </div>
                    </div>
                )}
            </section>
            <Footer />
        </>
    );
}
